// import styles from "./style.module.scss";
import React, { useState, useEffect } from 'react';
import DataTable from '../../components/DataTable';
import PageHeading from '../../components/PageHeading';
import { ANNOTATOR_API } from '../../api-handle';
import {
  LOCALSTORAGE_EVENT_FILTER_LABEL,
  LOCALSTORAGE_TOKEN_LABEL,
} from '../../constants';
import { useNavigate } from 'react-router-dom';
import { getHighestEventClass, LocalStorageHandler } from '../../utils';
import Chip from '../../components/Chip';
import useAllEventClasses from '../../hooks/useAllEventClasses';
import { useAppContext } from '../../context';
import {
  launchErrorNotification,
  launchSuccessNotification,
} from '../../utils/toasts';
import { Modal } from 'react-bootstrap';

const FILTERS_DEFAULT_NULLS = {
  startDate: null,
  endDate: null,
  is_annotated: null,
  location: null,
  predicted_class: null,
  predicted_score: null,
  annotated_class: null,
  device_serial: null,
  annotated_by: null,
};

const FILTERS_NAME_SET = {
  startDate: 'Start Date',
  endDate: 'End Date',
  is_annotated: 'Annotated Only',
  location: 'Location',
  predicted_class: 'Predicted Class',
  predicted_score: 'Predicted Score',
  annotated_class: 'Annotated Class',
  device_serial: 'Device Serial',
  annotated_by: 'Annotated By',
};

/**
 * Events component:
 * A page component showing all the events fetched from the API server in an
 * interactive data table. It also provides a filter section to filter out the
 * components
 */
export default function Events() {
  const { allDevices, allUsers, loadingGlobalAssets } = useAppContext();
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [data, setData] = useState([]);
  const [filters, setFilters] = useState(FILTERS_DEFAULT_NULLS);
  const [finalFilters, setFinalFilters] = useState({});
  const { allEventClasses, loadingAllEventClasses } = useAllEventClasses();
  const [allowFetching, setAllowFetching] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showDelConfirmModal, setShowDelConfirmModal] = useState(false);
  const [loadingDeletion, setLoadingDeletion] = useState(false);
  const navigate = useNavigate();

  const handleApplyFilters = () => {
    let temp_filters = {};
    for (let key in filters) {
      if (filters[key]) temp_filters[key] = filters[key];
    }
    setFinalFilters(temp_filters);

    LocalStorageHandler.set(LOCALSTORAGE_EVENT_FILTER_LABEL, temp_filters);
  };

  const handleClearFilters = () => {
    setFilters(FILTERS_DEFAULT_NULLS);
    setFinalFilters({});

    LocalStorageHandler.remove(LOCALSTORAGE_EVENT_FILTER_LABEL);
  };

  const handleRemoveOneFilter = (key) => {
    let temp_filters = { ...filters };
    temp_filters[key] = null;
    setFilters(temp_filters);

    let temp_final_filters = { ...finalFilters };
    delete temp_final_filters[key];
    setFinalFilters(temp_final_filters);

    LocalStorageHandler.set(
      LOCALSTORAGE_EVENT_FILTER_LABEL,
      temp_final_filters
    );
  };

  const handleDeleteEvents = () => {
    let tempArr = selectedRows;
    ANNOTATOR_API.deleteEvent(
      localStorage.getItem(LOCALSTORAGE_TOKEN_LABEL),
      selectedRows
    )
      .then(() => {
        setSelectedRows([]);
        let tempData = data.filter((item) => !tempArr.includes(item.id));
        setData(tempData);
        launchSuccessNotification('Events deleted successfully!');
        setShowDelConfirmModal(false);
        setLoadingDeletion(false);
      })
      .catch((err) => {
        launchErrorNotification('Error Occured!');
        setShowDelConfirmModal(false);
        setLoadingDeletion(false);
      });
  };

  useEffect(() => {
    let temp_filters = LocalStorageHandler.get(LOCALSTORAGE_EVENT_FILTER_LABEL);
    if (temp_filters) setFilters(temp_filters);

    let temp_final_filters = {};
    for (let key in temp_filters) {
      if (temp_filters[key]) temp_final_filters[key] = temp_filters[key];
    }
    setFinalFilters(temp_final_filters);
    setAllowFetching(true);
  }, []);

  useEffect(() => {
    if (allowFetching) {
      setIsLoading(true);
      ANNOTATOR_API.getAllEvents(
        window.localStorage.getItem(LOCALSTORAGE_TOKEN_LABEL),
        finalFilters,
        currentPage,
        pageSize
      )
        .then((res) => {
          setData(
            res.data.data.map((i) => {
              let c = getHighestEventClass(i.eventDetails);
              return {
                ...i,
                classLabel: c.className,
                originalScore: c.predictionScore,
                annotatedClass: i.annotation
                  ? i.annotation.annotations
                  : i.eventAnnotation
                  ? i.eventAnnotation.annotations
                  : null,
              };
            })
          );
          setTotalPages(res.data.pages);
          setTotalItems(res.data.total);
          setIsLoading(false);
        })
        .catch((err) => {
          console.log(err);
          setIsLoading(false);
        });
    }
  }, [currentPage, pageSize, finalFilters, allowFetching]);

  const columns = [
    {
      Header: 'Class Label',
      accessor: 'classLabel',
    },
    {
      Header: 'Place',
      accessor: 'location',
    },
    {
      Header: 'Predicted Score',
      accessor: 'originalScore',
    },
    {
      Header: 'Annotated Class',
      accessor: 'annotatedClass',
    },
    {
      Header: 'Length',
      accessor: 'recordingDuration',
    },
    {
      Header: 'Timestamp',
      accessor: 'createdAt',
    },
  ];

  return (
    <>
      <div className="container">
        <div className="bg-white rounded-15 shadow-sm px-3 pb-2">
          <PageHeading title="Events" />
          <DataTable
            isLoading={
              isLoading || loadingAllEventClasses || loadingGlobalAssets
            }
            filter
            sort
            data={data}
            showPagination
            showPageSizeChanger
            columns={columns}
            totalPages={totalPages}
            totalItems={totalItems}
            handleChangePageNo={(newPageNo) => {
              setCurrentPage((prev) => newPageNo);
            }}
            currentPageNo={currentPage}
            currentPageSize={pageSize}
            handleChangePageSize={(newPageSize) => {
              setPageSize((prev) => newPageSize);
            }}
            showClickableRows
            onRowClick={(id) => {
              navigate(`/event-details?id=${id}`);
            }}
            showMultiSelect={true}
            multiSelectRows={selectedRows}
            setMultiSelectRows={setSelectedRows}
            underSearchBarContent={
              <div>
                <div className="row mt-3 pt-2 border-top border-bottom pb-3">
                  <div className="col-12 col-md-4">
                    <label>Start Date</label>
                    <div className="input-group mb-3">
                      <span className="input-group-text ">
                        <i className="bi bi-calendar-date"></i>
                      </span>
                      <input
                        type="date"
                        className="form-control"
                        value={filters.startDate}
                        onChange={(e) => {
                          setFilters({
                            ...filters,
                            startDate: e.target.value,
                          });
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <label>End Date</label>
                    <div className="input-group mb-3">
                      <span className="input-group-text ">
                        <i className="bi bi-calendar-date"></i>
                      </span>
                      <input
                        type="date"
                        className="form-control"
                        value={filters.endDate}
                        onChange={(e) => {
                          setFilters({
                            ...filters,
                            endDate: e.target.value,
                          });
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <label>Location</label>
                    <div className="input-group mb-3">
                      <span className="input-group-text ">
                        <i className="bi bi-geo-alt"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Type to search..."
                        value={filters.location}
                        onChange={(e) => {
                          setFilters({
                            ...filters,
                            location: e.target.value,
                          });
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <label>Predicted Class</label>
                    <div className="input-group mb-3">
                      <span className="input-group-text ">
                        <i className="bi bi-tag"></i>
                      </span>
                      <select
                        className="form-control"
                        value={filters.predicted_class}
                        onChange={(e) => {
                          setFilters({
                            ...filters,
                            predicted_class: e.target.value,
                          });
                        }}
                      >
                        <option value={null}>-- Event Classes --</option>
                        {allEventClasses.map((i) => {
                          return <option value={i}>{i}</option>;
                        })}
                      </select>
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <label>Annotated Class</label>
                    <div className="input-group mb-3">
                      <span className="input-group-text ">
                        <i className="bi bi-tag"></i>
                      </span>
                      <select
                        className="form-control"
                        value={filters.annotated_class}
                        onChange={(e) => {
                          setFilters({
                            ...filters,
                            annotated_class: e.target.value,
                          });
                        }}
                      >
                        <option value={null}>-- Event Classes --</option>
                        {allEventClasses.map((i) => {
                          return <option value={i}>{i}</option>;
                        })}
                      </select>
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <label>Predicted Score</label>
                    <div className="input-group mb-3">
                      <span className="input-group-text ">
                        <i className="bi bi-123"></i>
                      </span>
                      <input
                        type="number"
                        placeholder="Type to search..."
                        className="form-control"
                        value={filters.predicted_score}
                        onChange={(e) => {
                          setFilters({
                            ...filters,
                            predicted_score: parseInt(e.target.value),
                          });
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <label>Device Serial</label>
                    <div className="input-group mb-3">
                      <span className="input-group-text ">
                        <i className="bi bi-tag"></i>
                      </span>
                      <select
                        className="form-control"
                        value={filters.device_serial}
                        onChange={(e) => {
                          setFilters({
                            ...filters,
                            device_serial: e.target.value,
                          });
                        }}
                      >
                        <option value={null}>-- Device Serials --</option>
                        {!loadingGlobalAssets &&
                          allDevices &&
                          allDevices.length > 0 &&
                          allDevices.map((i) => {
                            return (
                              <option value={i.serialNumber}>
                                {i.serialNumber}
                              </option>
                            );
                          })}
                      </select>
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <label>Annotated By</label>
                    <div className="input-group mb-3">
                      <span className="input-group-text ">
                        <i className="bi bi-tag"></i>
                      </span>
                      <select
                        className="form-control"
                        value={filters.annotated_by}
                        onChange={(e) => {
                          setFilters({
                            ...filters,
                            annotated_by: e.target.value,
                          });
                        }}
                      >
                        <option value={null}>-- Annotators --</option>
                        {!loadingGlobalAssets &&
                          allUsers &&
                          allUsers.length > 0 &&
                          allUsers
                            .filter((i) => i.role.toLowerCase() === 'annotator')
                            .map((i) => ({
                              id: i.id,
                              first_name: i.first_name,
                              last_name: i.last_name,
                            }))
                            .map((i) => {
                              return (
                                <option value={i.id}>
                                  {i.first_name + ' ' + i.last_name}
                                </option>
                              );
                            })}
                      </select>
                    </div>
                  </div>
                  <div className="col-12">
                    <br />
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={filters.is_annotated}
                        onChange={(e) => {
                          setFilters({
                            ...filters,
                            is_annotated: e.target.checked,
                          });
                        }}
                      />
                      <label className="form-check-label">
                        Show only annotated records?
                      </label>
                    </div>
                  </div>
                  <div className="col-12 d-flex gap-4 pt-3 align-items-center">
                    <div className="">
                      <div
                        className="btn btn-dark btn-sm"
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          handleApplyFilters();
                          setCurrentPage(1);
                        }}
                      >
                        <i className="bi bi-funnel-fill"></i> Apply{' '}
                      </div>
                    </div>
                    <div className="">
                      <div
                        className="link-dark"
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          handleClearFilters();
                        }}
                      >
                        <i className="bi bi-x"></i> Clear All Filters
                      </div>
                    </div>
                  </div>
                </div>
                {/* filter chips */}
                {Object.values(finalFilters).filter((f) => f).length > 0 && (
                  <div className="mt-4">
                    <div className="mb-2">
                      <b>
                        <i className="bi bi-funnel"></i> Filters Applied
                      </b>
                    </div>
                    <div className="d-flex gap-2">
                      {Object.keys(finalFilters)
                        .map((key, index) => {
                          if (finalFilters[key]) {
                            return (
                              <Chip
                                onClick={() => {
                                  handleRemoveOneFilter(key);
                                }}
                              >
                                {FILTERS_NAME_SET[key]}{' '}
                                {typeof finalFilters[key] === 'boolean'
                                  ? finalFilters[key]
                                  : ': ' + finalFilters[key]}
                              </Chip>
                            );
                          }
                          return null;
                        })
                        .filter((f) => f)}
                    </div>
                  </div>
                )}
              </div>
            }
            underSearchBarContentShowForNoItems={true}
          />
        </div>
      </div>

      {/* floating action button for delete */}
      {selectedRows.length > 0 && (
        <div className="position-fixed bottom-0 left-0 d-flex justify-content-end w-100 align-items-center p-5">
          <button
            className="btn btn-danger shadow"
            onClick={() => {
              setShowDelConfirmModal(true);
            }}
          >
            Delete <i className="bi bi-trash ms-1"></i>
          </button>
        </div>
      )}

      <Modal
        show={showDelConfirmModal}
        onHide={() => {
          setShowDelConfirmModal(false);
        }}
        keyboard={false}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmation</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p>Are you sure you want to delete the selected events?</p>
        </Modal.Body>

        <Modal.Footer>
          <button
            className="btn btn-danger"
            disabled={loadingDeletion}
            onClick={() => {
              setLoadingDeletion(true);
              handleDeleteEvents();
            }}
          >
            {!loadingDeletion ? (
              <>
                <i className="bi bi-trash"></i> Confirm Delete
              </>
            ) : (
              <>Deleting...</>
            )}
          </button>
          {!loadingDeletion && (
            <button className="btn btn-secondary">
              <i className="bi bi-x"></i> Cancle
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
}
